import React, { useState } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import DateInput from './DateInput';

export default function LeaveModal({ employee, onSave, onClose }) {
  useEscapeKey(onClose);
  const [form, setForm] = useState({
    leave_type: 'maternity',
    expected_birth_date: '',
    desired_leave_start_date: '',
    expected_return_date: '',
    pay_type: 'statutory',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isMaternityOrPaternity = form.leave_type === 'maternity' || form.leave_type === 'paternity';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError('');
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = [];
    if ((isMaternityOrPaternity || form.leave_type === 'adoption') && !form.expected_birth_date) {
      errors.push(form.leave_type === 'adoption' ? 'Expected placement date is required' : 'Expected birth date is required');
    }
    if ((isMaternityOrPaternity || form.leave_type === 'adoption') && !form.desired_leave_start_date) {
      errors.push('Desired leave start date is required');
    }
    if (!(isMaternityOrPaternity || form.leave_type === 'adoption') && !form.desired_leave_start_date) {
      errors.push('Start date is required');
    }
    if (errors.length > 0) {
      setError(errors.join('. '));
      return;
    }
    setSaving(true);
    try {
      // Send start_date as the desired leave start date for backward compatibility
      const saveData = {
        ...form,
        start_date: form.desired_leave_start_date,
      };
      await onSave(employee.id, saveData);
      onClose();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Parental Leave - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Leave Type</label>
              <select name="leave_type" value={form.leave_type} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                <option value="maternity">Maternity</option>
                <option value="paternity">Paternity</option>
                <option value="shared_parental">Shared Parental</option>
                <option value="adoption">Adoption</option>
              </select>
            </div>

            {(isMaternityOrPaternity || form.leave_type === 'adoption') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {form.leave_type === 'adoption' ? 'Expected Placement Date *' : 'Expected Birth Date *'}
                </label>
                <DateInput name="expected_birth_date" value={form.expected_birth_date} onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {isMaternityOrPaternity ? 'Desired Leave Start Date *' : 'Start Date *'}
              </label>
              <DateInput name="desired_leave_start_date" value={form.desired_leave_start_date} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Expected Return Date</label>
              <DateInput name="expected_return_date" value={form.expected_return_date} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Pay Type</label>
              <select name="pay_type" value={form.pay_type} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                <option value="statutory">Statutory</option>
                <option value="enhanced">Enhanced</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
              <textarea name="notes" rows="2" value={form.notes} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Any additional details..." />
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded text-sm text-blue-700 dark:text-blue-300">
              This will create a payroll flag for {form.pay_type === 'statutory' ? 'statutory' : 'enhanced'} {form.leave_type.replace('_', ' ')} pay calculations.
              {isMaternityOrPaternity && form.expected_birth_date && (
                <span className="block mt-1">Expected birth date: {new Date(form.expected_birth_date).toLocaleDateString('en-GB')}</span>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Record Leave'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
